import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//components
import { Button, Tooltip } from "@gpstrackit/react-controls";
import { Icon, IconButton, Typography } from '@mui/material';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';

//Views
import CreateEditNote from 'Modules/devices/.components/notesTab/.components/createEditNote';
import DeleteNote from 'Modules/devices/.components/notesTab/.components/deleteNote';

//Actions
import {
    notesCreateEditOpenModal,
    notesDeleteOpenModal,
    searchEntityRequest,
    clearRedux,
} from 'Redux/actions';

//Styles
import './index.scss';

//Helpers
import { getDateFormatted, idsValidation, copyToClipboard, getPMUser } from 'Core/data/Helpers';

import { HasPermission } from "Core/security"

const NotesTab = (props) => {
    const { deviceId, tab, height, className } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const openModalCreateEditNote = useSelector(state => state.createEditNoteRedux.openModal);
    const openModalDeleteNote = useSelector(state => state.deleteNoteRedux.openModal);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const lang = useSelector(state => state.commonRedux.language);
    const user = useSelector(state => state.securityRedux.user);

    const [selectedItems, setSelectedItems] = useState([]);
    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <IconButton
                        onClick={() => { copyToClipboard(item?.note, messages['common_copied']) }}
                        size="large">
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon">
                            content_copy
                        </Icon>
                    </IconButton>
                );
            }
        },
        {
            key: 'copy',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Icon className="toolbar-list-icon fs-20" style={{ cursor: "pointer" }} onClick={() => {
                        dispatch(notesCreateEditOpenModal(item.id, true));
                    }}>
                        remove_red_eye
                    </Icon>
                )
            }
        },
        {
            key: "note",
            field: "note",
            title: messages['notes_tab_note'],
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.note',
            code: "notes_tab_note",
            render: (item) => {
                return <span>
                    {
                        item?.note?.length > 50 &&
                        <Tooltip title={item?.note}>
                            <div>
                                {`${item?.note.substring(0, 50)}...`}
                            </div>
                        </Tooltip>
                    }
                    {
                        item?.note?.length <= 50 &&
                        item?.note
                    }
                </span>
            }
        },
        {
            key: "projectId",
            field: "projectId",
            title: messages['notes_tab_project'],
            show: true,
            resize: true,
            width: 100,
            sort: true,
            export: true,
            functionString: 'return row?.projectId',
            code: "notes_tab_project",
            render: (item) => {
                return <span>{item?.projectId}</span>
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['notes_tab_created_on'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "notes_tab_created_on",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "createdBy",
            field: "createdBy",
            title: messages['notes_tab_created_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
            `,
            code: "notes_tab_created_by",
            render: (item) => {
                return <span>{item?.createdBy && getPMUser(item?.createdBy, pmUsers)}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['notes_tab_updated_on'],
            show: true,
            resize: true,
            width: 200,
            sort: true,
            export: true,
            functionString: 'return row?.updatedOn && (new Date(`${row?.updatedOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "notes_tab_updated_on",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime")}</span>
            }
        },
        {
            key: "updatedBy",
            field: "updatedBy",
            title: messages['notes_tab_updated_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
            `,
            code: "notes_tab_updated_by",
            render: (item) => {
                return <span>{item?.updatedBy && getPMUser(item?.updatedBy, pmUsers)}</span>
            }
        }
    ]);

    useEffect(() => {
        (!pmUsers || pmUsers.length === 0) && dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, []);

    useEffect(() => {
        if (pmUsers.length > 0) {
            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                functionString: item.key === "createdBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
                ` : item.key === "updatedBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
                ` : item.functionString,
                render: item.key === "createdBy" ? (item) => {
                    let label = item?.createdBy ? getPMUser(item?.createdBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "updatedBy" ? (item) => {
                    let label = item?.updatedBy ? getPMUser(item?.updatedBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.render,
            }))
            setColumns([...currentColumns])
        }
    }, [pmUsers, lang])

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW CONFIGURATION OBJECTS
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * GraphQL configuration for entity notes
     */
    const graphqlConfig = useMemo(() => ({
        function: "searchEntityNotes",
        sortType: "EntityNoteSortInput",
        conditionsType: "EntityNoteSearchInput",
        dynamicQuery: true,
        customQuery: "id ",
    }), []);

    /**
     * Search bar configuration
     */
    const searchConfig = useMemo(() => ({
        conditions: ["note"],
        optionValue: "id",
        optionLabel: "note",
        custom: { fields: ["entity"], terms: [`Unit_${deviceId}`], exact: true },
        fields: ["note"],
    }), [deviceId]);

    /**
     * UI/Layout configuration
     */
    const uiConfig = useMemo(() => ({
        hideSplit: true,
        hideFilters: true,
        hidePagination: true,
        topHeight: props?.single ? 286 : 200,
        height: height && height + "px" || "500px",
    }), [height]);

    /**
     * Export configuration
     */
    const exportConfig = useMemo(() => ({
        fileName: "notes",
        noPDF: true,
    }), []);

    /**
     * Handle condition changes from search/filters
     */
    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort) => {
        let currentConditions = {}
        let currentTags = {}
        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }
        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'note' && item?.fields[0] !== 'entity');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            //New Items
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }
        let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");
        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.note))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: ["note"], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions.push({ fields: ["entity"], terms: [`Unit_${deviceId}`], exact: true, conditionOperator: "AND" });

        if (!conditions && tags) tableRef?.current?.getData({ newConditions: currentConditions });
        else { if (!total) tableRef?.current?.getData({ newConditions: currentConditions }) }
    }, [deviceId]);

    /**
     * Handle column changes (reorder, resize, visibility)
     */
    const handleChangeColumns = useCallback((items) => {
        setColumns(items);
    }, []);

    /**
     * Handle row selection
     */
    const handleSelectedItems = useCallback((items) => {
        setSelectedItems(items);
    }, []);

    /**
     * Context menu options for row actions
     */
    const menuOptions = useMemo(() => [
        {
            id: "notes-tab-edit",
            label: messages['edit'],
            show: true,
            disabled: !HasPermission(user, "note", "u"),
            single: true,
            action: () => {
                dispatch(notesCreateEditOpenModal(selectedItems[0].id));
            }
        },
        {
            id: "notes-tab-delete",
            label: messages['delete'],
            show: true,
            single: true,
            disabled: !HasPermission(user, "note", "d"),
            action: (items) => {
                let currentKey = items[0]
                dispatch(notesDeleteOpenModal(currentKey))
            }
        }
    ], [messages, user, selectedItems, dispatch]);

    /**
     * Render extra button component
     */
    const renderExtraButton = useCallback(() => {
        return (
            <Button
                className="btn-dark-blue add-new-note"
                disabled={!HasPermission(user, "note", "c")}
                onClick={() => {
                    dispatch(notesCreateEditOpenModal())
                }}
            >
                {messages['notes_tab_add_note']}
            </Button>
        )
    }, [user, messages, dispatch]);

    return (
        <div style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                name={`pm-notes-tab-${deviceId}`}
                entity="notes-device"
                columns={columns}
                infiniteScroll
                graphqlConfig={graphqlConfig}
                searchConfig={searchConfig}
                uiConfig={uiConfig}
                exportConfig={exportConfig}
                asyncSelect
                onSelectedItems={handleSelectedItems}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                options={menuOptions}
                extraButton={renderExtraButton}
            />

            {
                openModalCreateEditNote &&
                <CreateEditNote tableRef={tableRef} deviceId={deviceId} />
            }
            {
                openModalDeleteNote &&
                <DeleteNote tableRef={tableRef} />
            }
        </div>
    )
}
export default NotesTab;
